import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Alert, Typography, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import axios from 'axios';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../constants/api';
import styled from 'styled-components';

const { Title, Text } = Typography;

const ChangePasswordContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f5f5f5;
`;

const ChangePasswordCard = styled(Card)`
  width: 100%;
  max-width: 420px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  background: #ffffff;
  transform: translateY(-30px);
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const StyledTitle = styled(Title)`
  color: #262626 !important;
  margin-bottom: 8px !important;
  font-weight: 600 !important;
`;

const Subtitle = styled(Text)`
  color: #595959;
  font-size: 14px;
`;

const InfoBox = styled(Alert)`
  margin-bottom: 24px;
`;

const ChangePasswordPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const onFinish = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = authService.getToken();
      
      await axios.post(
        `${API_BASE_URL}/auth/change-password`,
        {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword
        },
        {
          headers: {
            'x-access-token': token
          }
        }
      );

      message.success('Password changed successfully!');
      
      // Update user data to remove requirePasswordChange flag
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        const updatedUser = { ...currentUser, requirePasswordChange: false };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        // Update context so ProtectedRoute immediately reflects new state
        setUser(updatedUser);
      }

      // Navigate immediately instead of with timeout
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ChangePasswordContainer>
      <ChangePasswordCard>
        <LogoContainer>
          <StyledTitle level={2}>Change Password</StyledTitle>
          <Subtitle>You must change your temporary password</Subtitle>
        </LogoContainer>

        <InfoBox
          message="Security Requirement"
          description="For your security, please update your temporary password to a new secure password."
          type="warning"
          showIcon
        />

        {error && (
          <Alert
            message={error}
            type="error"
            closable
            onClose={() => setError('')}
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          form={form}
          name="changePassword"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[
              { required: true, message: 'Please input your current password!' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your current password"
              size="large"
              style={{ height: '48px' }}
            />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: 'Please input your new password!' },
              { min: 6, message: 'Password must be at least 6 characters!' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your new password"
              size="large"
              style={{ height: '48px' }}
            />
          </Form.Item>

          <Form.Item
            label="Confirm New Password"
            name="confirmPassword"
            rules={[
              { required: true, message: 'Please confirm your new password!' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm your new password"
              size="large"
              style={{ height: '48px' }}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
              style={{ height: '48px' }}
            >
              Change Password
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
            Made with love 
            <span style={{ 
              color: '#ff4d4f', 
              display: 'inline-block',
              animation: 'heartbeat 1.2s ease-in-out infinite'
            }}>
              ❤️
            </span>
          </Text>
        </div>
        
        <style>{`
          @keyframes heartbeat {
            0%, 100% {
              transform: scale(1);
            }
            10%, 30% {
              transform: scale(1.1);
            }
            20%, 40% {
              transform: scale(0.95);
            }
          }
        `}</style>
      </ChangePasswordCard>
    </ChangePasswordContainer>
  );
};

export default ChangePasswordPage;
